import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Loader2,
  CheckCircle,
  Type,
  AlignLeft,
  List,
  Heading,
  PlusCircle,
  ArrowUp,
  ArrowDown,
  Gauge,
} from "lucide-react";
import { supabase } from "../../lib/supabase";
import { useToast } from "../../components/Toast";
import ConfirmDeleteDrawer from "../../components/ConfirmDeleteDrawer";

const INPUT_TYPES = [
  { value: "checkbox", label: "Checkbox" },
  { value: "yesno", label: "Sí/No" },
  { value: "text", label: "Texto" },
  { value: "structured", label: "Estructurado (formulario)" },
  { value: "skool_invite", label: "Skool Invite (sistema)" },
];

const SYSTEM_INPUT_TYPES = ["skool_invite"];

const FIELD_TYPES = [
  { value: "text", label: "Texto corto", icon: Type },
  { value: "textarea", label: "Texto largo", icon: AlignLeft },
  { value: "select", label: "Selector", icon: List },
  { value: "scale", label: "Escala 0-2", icon: Gauge },
  { value: "file", label: "Subir documento", icon: PlusCircle },
  { value: "heading", label: "Encabezado", icon: Heading },
];

const BLOCKS = [
  { name: "Tu punto de partida", order: 0 },
  { name: "Polaris", order: 1 },
  { name: "Núcleo", order: 2 },
  { name: "Sistema de Fidelización PRO", order: 3 },
  { name: "Libera tu tiempo", order: 4 },
];

function FieldEditor({
  field,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}) {
  const updateField = (key, value) => {
    onChange({ ...field, [key]: value });
  };

  const handleLabelChange = (label) => {
    const autoId = label
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");
    onChange({ ...field, label, id: autoId || field.id });
  };

  const FieldIcon =
    FIELD_TYPES.find((t) => t.value === field.type)?.icon || Type;

  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
      <div className="flex items-center gap-2">
        <div className="flex flex-col gap-0.5">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
          >
            <ArrowUp size={14} />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
          >
            <ArrowDown size={14} />
          </button>
        </div>

        <div className="w-8 h-8 rounded bg-white border border-gray-200 flex items-center justify-center text-gray-500">
          <FieldIcon size={16} />
        </div>

        <select
          value={field.type}
          onChange={(e) => updateField("type", e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white w-32"
        >
          {FIELD_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>

        <input
          type="text"
          value={field.label || ""}
          onChange={(e) => handleLabelChange(e.target.value)}
          placeholder="Etiqueta del campo"
          className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg"
        />

        <button
          type="button"
          onClick={onRemove}
          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
        >
          <X size={16} />
        </button>
      </div>

      {field.type === "textarea" && (
        <div className="mt-2 ml-12 flex items-center gap-2">
          <label className="text-xs text-gray-500">Filas:</label>
          <input
            type="number"
            value={field.rows || 3}
            onChange={(e) => updateField("rows", parseInt(e.target.value) || 3)}
            min={2}
            max={10}
            className="w-16 px-2 py-1 text-sm border border-gray-200 rounded"
          />
        </div>
      )}

      {field.type === "select" && (
        <div className="mt-2 ml-12">
          <label className="text-xs text-gray-500 block mb-1">
            Opciones (una por línea)
          </label>
          <textarea
            value={(field.options || []).join("\n")}
            onChange={(e) => updateField("options", e.target.value.split("\n"))}
            onBlur={(e) =>
              updateField(
                "options",
                e.target.value.split("\n").filter((o) => o.trim())
              )
            }
            rows={3}
            placeholder="Opción 1&#10;Opción 2&#10;Opción 3"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
          />
        </div>
      )}
    </div>
  );
}

function StructuredConfigEditor({ config, onChange }) {
  const [fields, setFields] = useState(config?.fields || []);
  const [description, setDescription] = useState(config?.description || "");

  const notifyChange = (newFields, newDesc) => {
    // Conserva claves extra de config (p. ej. levels de un cuestionario).
    onChange({ ...(config || {}), fields: newFields, description: newDesc });
  };

  const addField = (type) => {
    const newField = {
      id: `field_${Date.now()}`,
      type,
      label: "",
    };
    if (type === "textarea") newField.rows = 3;
    if (type === "select") newField.options = [];
    const newFields = [...fields, newField];
    setFields(newFields);
    notifyChange(newFields, description);
  };

  const updateField = (index, updatedField) => {
    const newFields = [...fields];
    newFields[index] = updatedField;
    setFields(newFields);
    notifyChange(newFields, description);
  };

  const removeField = (index) => {
    const newFields = fields.filter((_, i) => i !== index);
    setFields(newFields);
    notifyChange(newFields, description);
  };

  const moveField = (index, direction) => {
    const newFields = [...fields];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= fields.length) return;
    [newFields[index], newFields[newIndex]] = [
      newFields[newIndex],
      newFields[index],
    ];
    setFields(newFields);
    notifyChange(newFields, description);
  };

  const handleDescriptionChange = (newDesc) => {
    setDescription(newDesc);
    notifyChange(fields, newDesc);
  };

  return (
    <div className="space-y-4 border-t border-gray-200 pt-4 mt-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Descripción / Instrucciones
        </label>
        <textarea
          value={description}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          rows={3}
          placeholder="Instrucciones para el alumno..."
          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none text-sm"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-gray-700">
            Campos del formulario ({fields.length})
          </label>
          <div className="flex gap-1">
            {FIELD_TYPES.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => addField(t.value)}
                  title={`Añadir ${t.label}`}
                  className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                >
                  <Icon size={16} />
                </button>
              );
            })}
          </div>
        </div>

        {fields.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
            <PlusCircle size={32} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-gray-500">
              Usa los botones de arriba para añadir campos
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {fields.map((field, index) => (
              <FieldEditor
                key={index}
                field={field}
                onChange={(f) => updateField(index, f)}
                onRemove={() => removeField(index)}
                onMoveUp={() => moveField(index, -1)}
                onMoveDown={() => moveField(index, 1)}
                isFirst={index === 0}
                isLast={index === fields.length - 1}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ActivityDrawer({ activity, onSave, onClose, saving, isOpen }) {
  const [visible, setVisible] = useState(false);
  const [form, setForm] = useState({
    name: activity?.name || "",
    block_name: activity?.block_name || "Tu punto de partida",
    block_order: activity?.block_order ?? 0,
    input_type: activity?.input_type || "checkbox",
    link: activity?.link || "",
    description: activity?.description || "",
    sort_order: activity?.sort_order || 0,
    is_active: activity?.is_active ?? true,
    config: activity?.config || null,
  });

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setVisible(true));
    }
  }, [isOpen]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  };

  const handleBlockChange = (blockName) => {
    const block = BLOCKS.find((b) => b.name === blockName);
    setForm((f) => ({
      ...f,
      block_name: blockName,
      block_order: block?.order ?? 0,
    }));
  };

  const handleInputTypeChange = (inputType) => {
    setForm((f) => ({
      ...f,
      input_type: inputType,
      config: inputType === "structured" ? { fields: [], description: "" } : null,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    const dataToSave = { ...form };
    if (form.input_type !== "structured") {
      dataToSave.config = null;
    }

    onSave(dataToSave);
  };

  const content = (
    <>
      <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between bg-slate-900">
        <h2 className="font-semibold text-lg text-white">
          {activity ? "Editar Actividad" : "Nueva Actividad"}
        </h2>
        <button
          onClick={handleClose}
          className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex-1 overflow-y-auto p-5 space-y-4"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nombre *
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
            placeholder="Nombre de la actividad"
            required
            autoFocus
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Bloque
          </label>
          <select
            value={form.block_name}
            onChange={(e) => handleBlockChange(e.target.value)}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
          >
            {BLOCKS.map((b) => (
              <option key={b.name} value={b.name}>
                {b.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tipo de Input
          </label>
          <select
            value={form.input_type}
            onChange={(e) => handleInputTypeChange(e.target.value)}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
          >
            {INPUT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Link (opcional)
          </label>
          <input
            type="url"
            value={form.link}
            onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
            placeholder="https://..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descripción / Nota (opcional)
          </label>
          <textarea
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none resize-none"
            placeholder="Texto que se mostrará al alumno al abrir la actividad"
            rows={2}
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is_active"
            checked={form.is_active}
            onChange={(e) =>
              setForm((f) => ({ ...f, is_active: e.target.checked }))
            }
            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
          />
          <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
            Activa (visible para alumnos)
          </label>
        </div>

        {form.input_type === "structured" && (
          <StructuredConfigEditor
            config={form.config}
            onChange={(config) => setForm((f) => ({ ...f, config }))}
          />
        )}
      </form>

      <div className="px-5 py-4 border-t border-gray-200 flex gap-3 bg-gray-50">
        <button
          type="button"
          onClick={handleClose}
          className="flex-1 px-4 py-2.5 text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors font-medium"
        >
          Cancelar
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving || !form.name.trim()}
          className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors font-medium"
        >
          {saving ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <Save size={18} />
          )}
          Guardar
        </button>
      </div>
    </>
  );

  const drawer = (
    <div className="fixed inset-0 z-[99999]">
      <div
        className={`fixed inset-0 bg-black/40 transition-opacity duration-200 ${
          visible ? "opacity-100" : "opacity-0"
        }`}
        onClick={handleClose}
      />

      {/* Desktop: drawer from right */}
      <div
        className={`
          hidden md:flex fixed top-0 right-0 h-full w-full max-w-lg
          flex-col bg-white shadow-2xl
          transition-transform duration-200 ease-out
          ${visible ? "translate-x-0" : "translate-x-full"}
        `}
      >
        {content}
      </div>

      {/* Mobile: drawer from bottom */}
      <div
        className={`
          md:hidden fixed inset-0 bg-white
          flex flex-col
          transition-transform duration-200 ease-out
          ${visible ? "translate-y-0" : "translate-y-full"}
        `}
      >
        {content}
      </div>
    </div>
  );

  return createPortal(drawer, document.body);
}

function BlockSection({
  block,
  activities,
  onEdit,
  onDelete,
  onReorder,
  expandedBlocks,
  toggleBlock,
}) {
  const isExpanded = expandedBlocks.includes(block.name);
  const blockActivities = activities
    .filter((a) => a.block_name === block.name)
    .sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        onClick={() => toggleBlock(block.name)}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          <span className="font-semibold text-gray-900">{block.name}</span>
          <span className="text-sm text-gray-500">
            ({blockActivities.length} actividades)
          </span>
        </div>
      </button>

      {isExpanded && (
        <div className="divide-y divide-gray-100">
          {blockActivities.length === 0 ? (
            <p className="px-4 py-6 text-gray-500 text-center">
              No hay actividades en este bloque
            </p>
          ) : (
            blockActivities.map((activity, index) => {
              const isSystem = SYSTEM_INPUT_TYPES.includes(activity.input_type);
              return (
                <div
                  key={activity.id}
                  className={`px-4 py-2.5 flex items-center gap-2 hover:bg-gray-50 ${
                    !activity.is_active ? "opacity-50" : ""
                  }`}
                >
                  <div className="flex flex-col">
                    <button
                      onClick={() =>
                        index > 0 &&
                        onReorder(activity.id, blockActivities[index - 1].id)
                      }
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ArrowUp size={14} />
                    </button>
                    <button
                      onClick={() =>
                        index < blockActivities.length - 1 &&
                        onReorder(activity.id, blockActivities[index + 1].id)
                      }
                      disabled={index === blockActivities.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ArrowDown size={14} />
                    </button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {activity.name}
                      </p>
                      {isSystem && (
                        <span className="shrink-0 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-100 rounded-full">
                          Sistema
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      {INPUT_TYPES.find((t) => t.value === activity.input_type)
                        ?.label || activity.input_type}
                      {activity.input_type === "structured" &&
                        activity.config?.fields?.length > 0 &&
                        ` • ${activity.config.fields.length} campos`}
                      {activity.link && " • Con link"}
                      {!activity.is_active && " • Inactiva"}
                    </p>
                  </div>
                  {!isSystem && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => onEdit(activity)}
                        className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => onDelete(activity)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminActivities() {
  const { showSuccess, showError } = useToast();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedBlocks, setExpandedBlocks] = useState(BLOCKS.map((b) => b.name));

  const fetchActivities = async () => {
    const { data, error } = await supabase
      .from("activity_templates")
      .select("*")
      .order("sort_order");

    if (error) {
      showError("Error al cargar actividades");
      return;
    }
    setActivities(data || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchActivities();
  }, []);

  const toggleBlock = (blockName) => {
    setExpandedBlocks((prev) =>
      prev.includes(blockName)
        ? prev.filter((b) => b !== blockName)
        : [...prev, blockName]
    );
  };

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (editingActivity) {
        const { error } = await supabase
          .from("activity_templates")
          .update({
            ...formData,
            updated_at: new Date().toISOString(),
          })
          .eq("id", editingActivity.id);

        if (error) throw error;
        showSuccess("Actividad actualizada");
      } else {
        const blockActivities = activities.filter(
          (a) => a.block_name === formData.block_name
        );
        const maxOrder = blockActivities.length > 0
          ? Math.max(...blockActivities.map((a) => a.sort_order))
          : 0;

        const { error } = await supabase
          .from("activity_templates")
          .insert({ ...formData, sort_order: maxOrder + 1 });

        if (error) throw error;
        showSuccess("Actividad creada");
      }

      await fetchActivities();
      setShowForm(false);
      setEditingActivity(null);
    } catch (err) {
      showError("Error al guardar la actividad");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const { error } = await supabase
      .from("activity_templates")
      .delete()
      .eq("id", id);

    if (error) {
      showError("Error al eliminar la actividad");
      return;
    }

    showSuccess("Actividad eliminada");
    await fetchActivities();
    setDeleteTarget(null);
  };

  const handleEdit = (activity) => {
    setEditingActivity(activity);
    setShowForm(true);
  };

  const handleClose = () => {
    setShowForm(false);
    setEditingActivity(null);
  };

  const handleReorder = async (id1, id2) => {
    const act1 = activities.find((a) => a.id === id1);
    const act2 = activities.find((a) => a.id === id2);
    if (!act1 || !act2) return;

    const order1 = act1.sort_order;
    const order2 = act2.sort_order;

    setActivities((prev) =>
      prev.map((a) => {
        if (a.id === id1) return { ...a, sort_order: order2 };
        if (a.id === id2) return { ...a, sort_order: order1 };
        return a;
      })
    );

    const [res1, res2] = await Promise.all([
      supabase
        .from("activity_templates")
        .update({ sort_order: order2, updated_at: new Date().toISOString() })
        .eq("id", id1),
      supabase
        .from("activity_templates")
        .update({ sort_order: order1, updated_at: new Date().toISOString() })
        .eq("id", id2),
    ]);

    if (res1.error || res2.error) {
      showError("Error al reordenar");
      fetchActivities();
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-64 bg-gray-200 rounded" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 bg-gray-200 rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Gestión de Actividades
          </h1>
          <p className="text-gray-500">
            {activities.length} actividades en el programa
          </p>
        </div>
        <button
          onClick={() => {
            setEditingActivity(null);
            setShowForm(true);
          }}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2 transition-colors"
        >
          <Plus size={18} />
          Nueva Actividad
        </button>
      </div>

      <div className="space-y-3">
        {BLOCKS.map((block) => (
          <BlockSection
            key={block.name}
            block={block}
            activities={activities}
            onEdit={handleEdit}
            onDelete={setDeleteTarget}
            onReorder={handleReorder}
            expandedBlocks={expandedBlocks}
            toggleBlock={toggleBlock}
          />
        ))}
      </div>

      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <CheckCircle size={20} className="text-indigo-600 mt-0.5" />
          <div>
            <p className="font-medium text-indigo-900">
              Auto-sincronización activa
            </p>
            <p className="text-sm text-indigo-700">
              Al crear o modificar una actividad, se sincroniza automáticamente
              con todos los alumnos existentes.
            </p>
          </div>
        </div>
      </div>

      {showForm && (
        <ActivityDrawer
          key={editingActivity?.id || "new"}
          activity={editingActivity}
          onSave={handleSave}
          onClose={handleClose}
          saving={saving}
          isOpen={showForm}
        />
      )}

      {deleteTarget && (
        <ConfirmDeleteDrawer
          title="Eliminar actividad"
          itemName={deleteTarget.name}
          message="Esta acción eliminará el template. Las actividades ya asignadas a alumnos NO se eliminarán."
          onConfirm={() => handleDelete(deleteTarget.id)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
